import Mention from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import tippy from 'tippy.js';
import { CommentMentionList } from './commentMentionList';

/**
 * Build a configured @mention extension for the comment editor.
 *
 * The staff list is no longer filtered client-side here. Instead the popup
 * component (CommentMentionList) is Redux-connected and drives a paginated,
 * server-side staff search off the current `query`: it fetches page 1 on open /
 * on each new query and appends further pages as the list is scrolled — mirroring
 * the SelectAutocompletePagination pattern. So `items()` intentionally returns an
 * empty array; the popup owns its own data.
 *
 * The selected mention is stored as a node with attrs { id, label }; on submit
 * the doc is serialized to `@[id]` tokens (see commentUtils.serializeDocToText).
 */
export const buildCommentMention = () =>
    Mention.configure({
        HTMLAttributes: { class: 'comment-mention' },
        suggestion: {
            char: '@',
            // Data is fetched by the Redux-connected popup, not derived here.
            items: () => [],
            render: () => {
                let component;
                let popup;

                return {
                    onStart: (props) => {
                        component = new ReactRenderer(CommentMentionList, { props, editor: props.editor });
                        if (!props.clientRect) return;
                        popup = tippy('body', {
                            getReferenceClientRect: props.clientRect,
                            appendTo: () => document.body,
                            content: component.element,
                            showOnCreate: true,
                            interactive: true,
                            trigger: 'manual',
                            placement: 'bottom-start',
                            sticky: 'reference',
                            // Sit above every app layer (MUI Popover/Popper for the
                            // comments panel is ~1300; tippy default is 9999 but we
                            // pin it explicitly so the list never renders behind).
                            zIndex: 100000,
                            popperOptions: {
                                strategy: 'fixed',
                                modifiers: [
                                    // Flip above the caret when there isn't room below,
                                    // so the list is never clipped at the viewport edge.
                                    {
                                        name: 'flip',
                                        options: { fallbackPlacements: ['top-start', 'bottom-start'], boundary: 'viewport' },
                                    },
                                    {
                                        name: 'preventOverflow',
                                        options: { boundary: 'viewport', altAxis: true, padding: 8 },
                                    },
                                ],
                            },
                        });
                    },
                    onUpdate(props) {
                        component?.updateProps(props);
                        if (!props.clientRect) return;
                        popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect });
                    },
                    onKeyDown(props) {
                        if (props.event.key === 'Escape') {
                            popup?.[0]?.hide();
                            return true;
                        }
                        return component?.ref?.onKeyDown(props);
                    },
                    onExit() {
                        popup?.[0]?.destroy();
                        component?.destroy();
                    },
                };
            },
        },
    });
